import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, MapPin, Search, ChevronDown, LayoutGrid, FlipHorizontal, Heart, MessageCircle, User, ArrowLeft, CheckCircle, Award, DollarSign, ArrowRight, GalleryHorizontalEnd } from 'lucide-react';
import { Vendor } from '../../types/vendor.ts';
import { FavoritesAPI } from '../../services/api/favoritesApi.ts';
import SearchBar from './components/SearchBar.tsx';
import VendorSwipeView from './components/VendorSwipeView.tsx';
import MessagesSection from './components/MessagesSection.tsx';

// Add CSS for the sliding animations and utility classes
const customStyles = `
@keyframes slide-in-from-right {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes slide-in-from-left {
  from { transform: translateX(-100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes slide-out-to-left {
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(-100%); opacity: 0; }
}

@keyframes slide-out-to-right {
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(100%); opacity: 0; }
}

.slide-in-from-right {
  animation: slide-in-from-right 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.slide-in-from-left {
  animation: slide-in-from-left 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.slide-out-to-left {
  animation: slide-out-to-left 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

.slide-out-to-right {
  animation: slide-out-to-right 300ms cubic-bezier(0.4, 0, 0.2, 1) forwards;
}

/* Hide scrollbar but maintain functionality */
.no-scrollbar::-webkit-scrollbar {
  display: none;
}

.no-scrollbar {
  -ms-overflow-style: none;  /* IE and Edge */
  scrollbar-width: none;  /* Firefox */
}
`;

// Add the styles to the document head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = customStyles;
  document.head.appendChild(styleElement);
}

// Journey categories
const journeyCategories = [
  'Venues',
  'Photography',
  'Decor',
  'Florists',
  'Catering',
  'Entertainment',
  'Transport',
  'Unique',
  'Wedding Planner'
];

// All available theme images
const allThemeImages = [
  '/themesOption/Indoor.jpg',
  '/themesOption/Outdoor.jpg',
  '/themesOption/Vineyard.jpg',
  '/themesOption/Beach.jpg',
  '/themesOption/Classic.jpg',
  '/themesOption/Modern.jpg',
  '/themesOption/Minimalistic.jpg',
  '/themesOption/Rustic.jpg',
  '/themesOption/Bohemian.jpg',
  '/themesOption/Tropical.jpg',
  '/themesOption/Bouquet.jpg',
  '/themesOption/Woodland.jpg',
  '/themesOption/Luxury.jpg',
  '/themesOption/Mandap.jpg',
  '/themesOption/Proposal.jpg'
];

// Helper function to normalize vendor type
const normalizeVendorType = (type: string): string => {
  // Convert to title case and handle plurals
  const normalized = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  
  // Handle special cases
  switch(normalized) {
    case 'Venue': return 'Venues';
    case 'Photographer': return 'Photography';
    case 'Florist': return 'Florists';
    case 'Caterer': return 'Catering';
    case 'Dj': return 'Entertainment';
    case 'Transportation': return 'Transport';
    case 'Planner': return 'Wedding Planner';
    default: return normalized;
  }
};

// Helper function to get a consistent theme image based on vendor ID
const getThemeImage = (vendorId: string | number): string => {
  // Use vendor ID to select a consistent image
  const numericId = typeof vendorId === 'string' 
    ? vendorId.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0) 
    : Number(vendorId);
  
  // Use modulo to get an index within the array bounds
  const imageIndex = numericId % allThemeImages.length;
  
  return allThemeImages[imageIndex];
};

interface ConnectMobileProps {
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  vendorData: Record<string, Vendor[]>;
  isLoading: boolean;
  handleSelectVendor: (vendor: Vendor) => void;
}

interface ConnectMobileState {
  showFilters: boolean;
  viewMode: 'list' | 'swipe';
  favoriteVendors: Vendor[];
  showingFavorites: boolean;
  loadingFavorites: boolean;
  showingMessages: boolean;
  messages: any[]; // Replace with proper Message type
  isViewTransitioning: boolean;
}

interface VendorSectionState {
  prevViewMode: 'list' | 'swipe';
  prevCategory: string;
  isViewTransitioning: boolean;
}

interface Message {
  id: string;
  vendorName: string;
  vendorType: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  vendorImage?: string;
}

interface MainContentProps {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  showFilters: boolean;
  setShowFilters: (show: boolean) => void;
  vendorData: Record<string, Vendor[]>;
  selectedCategory: string;
  setSelectedCategory: (category: string) => void;
  viewMode: 'list' | 'swipe';
  setViewMode: (mode: 'list' | 'swipe') => void;
  showingFavorites: boolean;
  setShowingFavorites: (show: boolean) => void;
  favoriteVendors: Vendor[];
  loadingFavorites: boolean;
  isLoading: boolean;
  handleSelectVendor: (vendor: Vendor) => void;
  showingMessages: boolean;
  messages: any[];
  onMessageClick: (message: any) => void;
  toggleMessages: () => void;
}

interface MainContentState {
  isViewTransitioning: boolean;
}

/* 
* VendorCard Component
* Displays a single vendor card with all relevant information
*/
class VendorCard extends React.Component<{
  vendor: Vendor;
  handleSelectVendor: (vendor: Vendor) => void;
  isFavoriteView?: boolean;
}, {
  isLiked: boolean;
  imageLoaded: boolean;
  showOptions: boolean;
}> {
  state = {
    isLiked: false,
    imageLoaded: false,
    showOptions: false
  };
  
  // Format vendor type for display
  formatVendorType(type: string) {
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  }

  // Format price to display in a readable way
  formatPrice(price: number | undefined) {
    if (!price) return 'Price on request';
    return `$${price.toLocaleString()}`;
  }

  // Extract services for display
  getServicesList(vendor: Vendor) {
    if (!vendor.services || vendor.services.length === 0) return null;
    
    // Get the first 2 service types to display
    const servicesToShow = vendor.services
      .slice(0, 2)
      .map(service => service.serviceType)
      .filter(Boolean);
      
    return servicesToShow.length > 0 ? servicesToShow.join(', ') : null;
  }

  // Get height class for card
  getCardHeight() {
    const { vendor } = this.props;
    // Create varying heights for masonry effect
    if (vendor.topVendor && vendor.verified) {
      return 'aspect-[4/6]'; // Taller for premium vendors
    } else if (vendor.topVendor || vendor.verified) {
      return 'aspect-[4/5]'; // Medium-tall for special vendors
    }
    return 'aspect-[1/1]'; // Default height
  }

  handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when clicking favorite button
    const { vendor } = this.props;
    const { isLiked } = this.state;

    try {
      if (isLiked) {
        await FavoritesAPI.removeVendorFromFavorites(String(vendor.id));
      } else {
        await FavoritesAPI.addVendorToFavorites(String(vendor.id));
      }
      this.setState({ isLiked: !isLiked });
    } catch (error) {
      console.error('Failed to update favorite status:', error);
    }
  };

  toggleOptions = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    this.setState(prevState => ({ showOptions: !prevState.showOptions }));
  };
  
  handleDeleteVendor = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    const { vendor } = this.props;
    
    try {
      // Replace the global confirm with a direct deletion
      // We'll assume the confirmation is handled by the dropdown UI
      await FavoritesAPI.deleteVendor(String(vendor.id));
      // Show success message without using alert
      // You might want to use a toast notification in a real app
      this.setState({ showOptions: false });
      // You might want to refresh the vendor list here
      // or use a callback to notify parent component
    } catch (error) {
      console.error('Failed to delete vendor:', error);
    }
  };

  render() {
    const { vendor, handleSelectVendor, isFavoriteView = false } = this.props;
    const { isLiked, imageLoaded, showOptions } = this.state;
    const heightClass = this.getCardHeight();
    const vendorImage = getThemeImage(vendor.id);
    const services = this.getServicesList(vendor);

    return (
      <div
        className={`vendor-card relative ${heightClass} rounded-xl overflow-hidden shadow-lg cursor-pointer border border-white/20 backdrop-blur-sm hover:scale-[1.02] transition-transform duration-200`}
        onClick={() => handleSelectVendor(vendor)}
        data-section="vendor-card"
      >
        {/* Background Image */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10" />
          <img
            src={vendorImage}
            alt={vendor.name}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => this.setState({ imageLoaded: true })}
          />
          <div className={`absolute inset-0 bg-black/20 backdrop-blur-sm ${imageLoaded ? 'opacity-0' : 'opacity-100'}`} />
        </div>
        
        {/* Top Badges Row */}
        <div className="absolute top-1.5 left-1.5 right-1.5 flex flex-wrap gap-1 z-20">
          {vendor.verified && (
            <div className="px-1.5 py-0.5 rounded-full bg-[#2DCEA8]/80 backdrop-blur-sm flex items-center gap-0.5 border border-[#2DCEA8]/30">
              <CheckCircle className="w-2.5 h-2.5 text-white" />
            </div>
          )}
          {vendor.topVendor && (
            <div className="px-1.5 py-0.5 rounded-full bg-[#8A2BE2]/80 backdrop-blur-sm flex items-center gap-0.5 border border-[#8A2BE2]/30">
              <Award className="w-2.5 h-2.5 text-white" />
            </div>
          )}
          {vendor.approximateCharge && (
            <div className="px-1.5 py-0.5 rounded-full bg-[#FFB02E]/80 backdrop-blur-sm border border-[#FFB02E]/30">
              <span className="text-[10px] font-medium text-white">${Math.round(vendor.approximateCharge/1000)}K</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="absolute top-1.5 right-1.5 z-30 flex gap-1">
          {/* Favorite Button - Only show when not in favorites view */}
          {!isFavoriteView && (
            <button
              onClick={this.handleFavoriteClick}
              className={`p-2 rounded-full backdrop-blur-sm transition-all duration-200 border ${
                isLiked 
                  ? 'bg-[#FF4E8E] border-[#FF4E8E]/30' 
                  : 'bg-black/20 border-white/30 hover:bg-[#FF4E8E]/20'
              }`}
            >
              <Heart 
                className={`w-3.5 h-3.5 transition-colors ${
                  isLiked ? 'text-white fill-current' : 'text-white'
                }`}
              />
            </button>
          )}
          
          {/* Delete Button - Only for favorite view */}
          {isFavoriteView && (
            <button
              onClick={this.handleDeleteVendor}
              className="p-2 rounded-full bg-[#FF6B6B]/80 backdrop-blur-sm border border-[#FF6B6B]/30 hover:bg-[#FF6B6B] transition-all"
            >
              <svg className="w-3.5 h-3.5 text-white" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
        
        {/* Main Content */}
        <div className="absolute bottom-0 left-0 right-0 p-2 z-20 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
          <div className="flex flex-col gap-1">
            {/* Title and Location */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-0.5 line-clamp-1">{vendor.name}</h3>
              {vendor.address && (
                <div className="flex items-center gap-0.5">
                  <MapPin className="w-2.5 h-2.5 text-white/90 flex-shrink-0" />
                  <p className="text-[10px] text-white/90 truncate">{vendor.address.split(',')[0]}</p>
                </div>
              )}
            </div>
            
            {/* Rating and Services */}
            <div className="flex flex-col gap-1">
              {vendor.rating && (
                <div className="flex items-center gap-1">
                  <div className="flex flex-shrink-0">
                    {[...Array(5)].map((_, i) => {
                      const rating = vendor.googlePlaceData?.rating || vendor.rating || 5;
                      return (
                        <Star 
                          key={i} 
                          className={`w-2.5 h-2.5 ${i < Math.round(rating) ? 'fill-amber-300 text-amber-300' : 'text-white/40'}`} 
                        />
                      );
                    })}
                  </div>
                  <span className="text-[10px] text-white ml-0.5">
                    {vendor.rating?.toFixed(1) || "5.0"}
                  </span>
                </div>
              )}

              {/* Bio excerpt if available */}
              {vendor.bio && (
                <p className="text-[9px] text-white/70 line-clamp-2 max-h-[24px] overflow-hidden">
                  {vendor.bio}
                </p>
              )}

              <div className="flex items-center justify-between gap-1.5 mt-0.5">
                <div className="px-1.5 py-0.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/10">
                  <span className="text-[10px] font-medium text-white line-clamp-1">
                    {this.formatVendorType(vendor.vendorType)}
                  </span>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectVendor(vendor);
                  }}
                  className="px-2 py-0.5 rounded-full bg-[#FF4E8E] text-white text-[10px] font-medium hover:bg-[#FF4E8E]/90 transition-all flex items-center gap-1 flex-shrink-0 border border-[#FF4E8E]/30"
                >
                  View
                  <ArrowRight className="w-2.5 h-2.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

/* 
* VendorListCard Component
* Displays a horizontal vendor card for list view
*/
class VendorListCard extends React.Component<{
  vendor: Vendor;
  handleSelectVendor: (vendor: Vendor) => void;
}> {
  state = {
    isLiked: false,
    imageLoaded: false
  };

  formatVendorType(type: string) {
    return type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  }

  getCardHeight() {
    const { vendor } = this.props;
    // Create varying heights for masonry effect
    if (vendor.topVendor && vendor.verified) {
      return 'aspect-[4/6]'; // Taller for premium vendors
    } else if (vendor.topVendor || vendor.verified) {
      return 'aspect-[4/5]'; // Medium-tall for special vendors
    }
    return 'aspect-[1/1]'; // Default height
  }

  handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when clicking favorite button
    const { vendor } = this.props;
    const { isLiked } = this.state;

    try {
      if (isLiked) {
        await FavoritesAPI.removeVendorFromFavorites(String(vendor.id));
      } else {
        await FavoritesAPI.addVendorToFavorites(String(vendor.id));
      }
      this.setState({ isLiked: !isLiked });
    } catch (error) {
      console.error('Failed to update favorite status:', error);
    }
  };

  render() {
    const { vendor, handleSelectVendor } = this.props;
    const { isLiked, imageLoaded } = this.state;
    const heightClass = this.getCardHeight();
    const vendorImage = getThemeImage(vendor.id);

    return (
      <div
        className={`vendor-list-card relative w-full ${heightClass} rounded-xl overflow-hidden shadow-lg cursor-pointer border border-white/20 backdrop-blur-sm hover:scale-[1.02] transition-transform duration-200 mb-3`}
        onClick={() => handleSelectVendor(vendor)}
        data-section="vendor-list-card"
      >
        {/* Background Image with Gradient */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent z-10" />
          <img
            src={vendorImage}
            alt={vendor.name}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
            onLoad={() => this.setState({ imageLoaded: true })}
          />
          <div className={`absolute inset-0 bg-black/20 backdrop-blur-sm ${imageLoaded ? 'opacity-0' : 'opacity-100'}`} />
        </div>
        
        {/* Top Badges Row */}
        <div className="absolute top-1.5 left-1.5 right-1.5 flex flex-wrap gap-1 z-20">
          {vendor.verified && (
            <div className="px-1.5 py-0.5 rounded-full bg-[#FF4E8E]/80 backdrop-blur-sm flex items-center gap-0.5 border border-[#FF4E8E]/30">
              <CheckCircle className="w-2.5 h-2.5 text-white" />
            </div>
          )}
          {vendor.topVendor && (
            <div className="px-1.5 py-0.5 rounded-full bg-[#8A2BE2]/80 backdrop-blur-sm flex items-center gap-0.5 border border-[#8A2BE2]/30">
              <Award className="w-2.5 h-2.5 text-white" />
            </div>
          )}
          <div className="px-1.5 py-0.5 rounded-full bg-[#FFB02E]/80 backdrop-blur-sm border border-[#FFB02E]/30">
            <span className="text-[10px] font-medium text-white">$$$</span>
          </div>
        </div>

        {/* Favorite Button */}
        <button
          onClick={this.handleFavoriteClick}
          className={`absolute top-1.5 right-1.5 z-30 p-2 rounded-full backdrop-blur-sm transition-all duration-200 border ${
            isLiked 
              ? 'bg-[#FF4E8E] border-[#FF4E8E]/30' 
              : 'bg-black/20 border-white/30 hover:bg-[#FF4E8E]/20'
          }`}
        >
          <Heart 
            className={`w-3.5 h-3.5 transition-colors ${
              isLiked ? 'text-white fill-current' : 'text-white'
            }`}
          />
        </button>
        
        {/* Main Content */}
        <div className="absolute bottom-0 left-0 right-0 p-2 z-20 bg-gradient-to-t from-black/90 via-black/50 to-transparent">
          <div className="flex flex-col gap-1">
            {/* Title and Location */}
            <div>
              <h3 className="text-sm font-semibold text-white mb-0.5 line-clamp-1">{vendor.name}</h3>
              <div className="flex items-center gap-0.5">
                <MapPin className="w-2.5 h-2.5 text-white/90 flex-shrink-0" />
                <p className="text-[10px] text-white/90 truncate">{vendor.location}</p>
              </div>
            </div>
            
            {/* Rating and Category */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-1">
                <div className="flex flex-shrink-0">
                  {[...Array(5)].map((_, i) => {
                    const rating = vendor.googlePlaceData?.rating || vendor.rating || 5;
                    return (
                      <Star 
                        key={i} 
                        className={`w-2.5 h-2.5 ${i < Math.round(rating) ? 'fill-amber-300 text-amber-300' : 'text-white/40'}`} 
                      />
                    );
                  })}
                </div>
                <span className="text-[10px] text-white ml-0.5">
                  {vendor.rating?.toFixed(1) || "5.0"}
                </span>
              </div>

              <div className="flex items-center justify-between gap-1.5">
                <div className="px-1.5 py-0.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/10">
                  <span className="text-[10px] font-medium text-white line-clamp-1">
                    {this.formatVendorType(vendor.vendorType)}
                  </span>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleSelectVendor(vendor);
                  }}
                  className="px-2 py-0.5 rounded-full bg-[#FF4E8E] text-white text-[10px] font-medium hover:bg-[#FF4E8E]/90 transition-all flex items-center gap-1 flex-shrink-0 border border-[#FF4E8E]/30"
                >
                  View
                  <ArrowRight className="w-2.5 h-2.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

/* 
* VendorSection Component
* Handles the display of vendors based on current view mode
*/
class VendorSection extends React.Component<{
  selectedCategory: string;
  vendorData: Record<string, Vendor[]>;
  isLoading: boolean;
  viewMode: 'list' | 'swipe';
  showingFavorites: boolean;
  favoriteVendors: Vendor[];
  loadingFavorites: boolean;
  handleSelectVendor: (vendor: Vendor) => void;
  setSelectedCategory: (category: string) => void;
}, VendorSectionState> {
  state: VendorSectionState = {
    prevViewMode: 'list',
    prevCategory: '',
    isViewTransitioning: false
  };

  componentDidMount() {
    this.setState({
      prevViewMode: this.props.viewMode,
      prevCategory: this.props.selectedCategory
    });
  }

  componentDidUpdate(prevProps: any) {
    const { viewMode, selectedCategory } = this.props;
    const { prevViewMode, prevCategory } = this.state;

    if (prevViewMode !== viewMode || prevCategory !== selectedCategory) {
      this.setState({ 
        isViewTransitioning: true,
        prevViewMode: viewMode,
        prevCategory: selectedCategory
      });

      setTimeout(() => {
        this.setState({ isViewTransitioning: false });
      }, 300);
    }
  }

  renderListView = () => {
    const { vendorData, selectedCategory, isLoading, handleSelectVendor } = this.props;
    const { isViewTransitioning } = this.state;
    
    if (isLoading || isViewTransitioning) {
      return (
        <div className="flex justify-center items-center h-full py-6">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#FF4E8E] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-[#5D5FEF]/70">Loading vendors...</p>
          </div>
        </div>
      );
    }

    const vendors = vendorData[selectedCategory] || [];
    
    if (vendors.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-10">
          <div className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20">
            <p className="text-white/80 text-center text-sm">No vendors found in this category</p>
          </div>
        </div>
      );
    }

    return (
      <div className="columns-2 sm:columns-3 gap-3 px-2 pb-24">
        {vendors.map((vendor) => (
          <div key={vendor.id} className="break-inside-avoid mb-3">
            <VendorListCard
              vendor={vendor}
              handleSelectVendor={handleSelectVendor}
            />
          </div>
        ))}
      </div>
    );
  };

  renderFavorites = () => {
    const { favoriteVendors, loadingFavorites, handleSelectVendor } = this.props;
    
    if (loadingFavorites) {
      return (
        <div className="flex justify-center items-center h-full py-6">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#FF4E8E] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-[#5D5FEF]/70">Loading favorites...</p>
          </div>
        </div>
      );
    }
    
    if (favoriteVendors.length > 0) {
      return (
        <div className="columns-2 sm:columns-3 gap-3 px-2 pb-20 [column-fill:_balance]">
          {favoriteVendors.map((vendor) => (
            <div key={vendor.id} className="break-inside-avoid mb-3">
              <VendorCard 
                vendor={vendor} 
                handleSelectVendor={handleSelectVendor}
                isFavoriteView
              />
            </div>
          ))}
        </div>
      );
    }
    
    return (
      <div className="flex flex-col items-center justify-center py-6 px-4 bg-white/60 backdrop-blur-md rounded-xl border border-white/40">
        <Heart className="w-6 h-6 text-gray-300 mb-2" />
        <p className="text-[#5D5FEF]/80 text-center text-xs">No favorite vendors found. Start adding vendors to your favorites!</p>
      </div>
    );
  };

  renderSwipeView = () => {
    const { vendorData, selectedCategory, handleSelectVendor } = this.props;
    const { isViewTransitioning } = this.state;
    
    if (isViewTransitioning) {
      return (
        <div className="flex justify-center items-center h-full py-6">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-[#FF4E8E] border-t-transparent rounded-full animate-spin"></div>
            <p className="text-sm text-[#5D5FEF]/70">Loading vendors...</p>
          </div>
        </div>
      );
    }

    const currentVendors = vendorData[selectedCategory] || [];
    
    return (
      <VendorSwipeView
        vendors={currentVendors}
        onVendorSelect={handleSelectVendor}
        onMessageVendor={(vendor) => {
          console.log('Message vendor:', vendor);
        }}
        onViewDetails={(vendor) => {
          handleSelectVendor(vendor);
        }}
      />
    );
  };

  render() {
    const { showingFavorites, viewMode } = this.props;
    const { isViewTransitioning } = this.state;
    
    if (showingFavorites) {
      return this.renderFavorites();
    }
    
    return (
      <div 
        className={`vendor-section ${
          viewMode === 'list' 
            ? 'h-[calc(100vh-200px)] overflow-auto' 
            : 'h-[calc(100vh-287px)] flex items-center justify-center'
        } pb-safe transition-all duration-300`}
        style={{
          opacity: isViewTransitioning ? 0 : 1,
          transform: isViewTransitioning ? 'scale(0.98)' : 'scale(1)',
          transition: 'opacity 0.3s ease, transform 0.3s ease'
        }}
      >
        {viewMode === 'list' ? this.renderListView() : this.renderSwipeView()}
      </div>
    );
  }
}

/* 
* MainContent Component
* Contains the main content area and search functionality
*/
class MainContent extends React.Component<MainContentProps, MainContentState> {
  state: MainContentState = {
    isViewTransitioning: false
  };

  renderCategoryNav() {
    const { showingFavorites, selectedCategory, setSelectedCategory, viewMode } = this.props;
    
    if (showingFavorites) {
      return null;
    }

    const currentIndex = journeyCategories.indexOf(selectedCategory);
    
    return (
      <nav 
        className="category-section px-2 mb-1 overflow-x-auto no-scrollbar relative"
        data-section="category-section"
        aria-label="Categories"
      >
        <div className="flex space-x-0.5 pb-1 pt-1 scrollbar-hide">
          {journeyCategories.map((category, index) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`flex-none whitespace-nowrap px-2 py-0.5 rounded-full text-[13px] font-medium transition-all duration-300 relative
                ${selectedCategory === category
                  ? 'bg-[#FF4E8E] text-white shadow-sm scale-110 transform'
                  : `text-[#5D5FEF] border border-white/30 ${
                      Math.abs(index - currentIndex) <= 1 ? 'opacity-100' : 'opacity-70'
                    }`
                }
              `}
              style={{
                transform: selectedCategory === category 
                  ? 'scale(1.1)' 
                  : `scale(${1 - Math.min(0.15, Math.abs(index - currentIndex) * 0.05)})`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              {category}
            </button>
          ))}
        </div>
        {/* Category Indicator */}
        {viewMode === 'list' && (
          <div className="flex justify-center items-center gap-1 mt-1">
            <div className="flex items-center justify-center gap-0.5">
              {journeyCategories.map((_, index) => (
                <div 
                  key={index}
                  className={`h-1 rounded-full transition-all duration-300 ${
                    index === currentIndex 
                      ? 'w-3 bg-[#FF4E8E]' 
                      : 'w-1 bg-[#5D5FEF]/30'
                  }`}
                ></div>
              ))}
            </div>
          </div>
        )}
      </nav>
    );
  }

  renderSearchBar() {
    const { 
      searchQuery, 
      setSearchQuery, 
      showFilters, 
      setShowFilters, 
      vendorData,
      showingFavorites,
      setShowingFavorites,
      viewMode,
      setViewMode
    } = this.props;
    
    return (
      <div className="px-2 mb-1 flex items-center gap-1 sticky top-1 z-10 backdrop-blur-sm pb-1 pt-1">
        {showingFavorites && (
          <button
            onClick={() => setShowingFavorites(false)}
            className="p-1.5 rounded-full bg-white/50 backdrop-blur-sm active:bg-white/60 transition-colors flex-shrink-0 border border-white/30"
            data-section="back-button"
          >
            <ArrowLeft className="w-4 h-4 text-[#5D5FEF]" />
          </button>
        )}
        
        <div className="flex-1">
          <SearchBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            vendors={vendorData}
          />
        </div>
        
        {!showingFavorites && (
          <button
            onClick={() => setViewMode(viewMode === 'list' ? 'swipe' : 'list')}
            className="p-1.5 rounded-full bg-white/50 backdrop-blur-sm active:bg-white/60 transition-colors flex-shrink-0 border border-white/30"
            data-section="view-mode-toggle"
          >
            {viewMode === 'list' ? (
              <GalleryHorizontalEnd className="w-4 h-4 text-[#5D5FEF]" />
            ) : (
              <LayoutGrid className="w-4 h-4 text-[#5D5FEF]" />
            )}
          </button>
        )}
      </div>
    );
  }

  render() {
    const {
      selectedCategory,
      vendorData,
      isLoading,
      viewMode,
      showingFavorites,
      favoriteVendors,
      loadingFavorites,
      handleSelectVendor,
      setSelectedCategory,
      showingMessages,
      onMessageClick,
      toggleMessages
    } = this.props;
    
    return (
      <main 
        className="main-content min-h-screen bg-gradient-to-b from-[#FF69B4]/10 to-[#FFDAB9]/10 pt-16 pb-20 z-0 overflow-auto no-scrollbar"
        data-section="main-content"
        id="main-content"
      >
        {/* Search Bar */}
        {this.renderSearchBar()}

        {/* Category Scroll - Only show if not in messages view */}
        {!showingMessages && this.renderCategoryNav()}

        <section className="content-section mt-1">
          <div className="h-full">
            {showingMessages ? (
              <div className="px-4 py-3">
                <div className="flex items-center gap-3 mb-4">
                  <button
                    onClick={toggleMessages}
                    className="p-1.5 rounded-full bg-white/50 backdrop-blur-sm active:bg-white/60 transition-colors flex-shrink-0 border border-white/30"
                  >
                    <ArrowLeft className="w-4 h-4 text-[#5D5FEF]" />
                  </button>
                  <h2 className="text-lg font-semibold text-[#5D5FEF]">Your Inquiries</h2>
                </div>
                <MessagesSection 
                  onMessageClick={onMessageClick}
                />
              </div>
            ) : (
              <div className="relative z-0">
                <VendorSection
                  selectedCategory={selectedCategory}
                  vendorData={vendorData}
                  isLoading={isLoading}
                  viewMode={viewMode}
                  showingFavorites={showingFavorites}
                  favoriteVendors={favoriteVendors}
                  loadingFavorites={loadingFavorites}
                  handleSelectVendor={handleSelectVendor}
                  setSelectedCategory={setSelectedCategory}
                />
              </div>
            )}
          </div>
        </section>
      </main>
    );
  }
}

/* 
* BottomNav Component
* Fixed navigation at bottom of screen
*/
class BottomNav extends React.Component<{
  showingFavorites: boolean;
  fetchFavoriteVendors: () => void;
  showingMessages: boolean;
  toggleMessages: () => void;
}> {
  render() {
    const { showingFavorites, fetchFavoriteVendors, showingMessages, toggleMessages } = this.props;
    
    return (
      <div 
        className="bottom-nav fixed bottom-0 left-0 right-0 bg-white/60 backdrop-blur-md border-t border-white/30 py-1 flex justify-around items-center z-40 h-16 pb-safe"
        data-section="bottom-nav"
        id="bottom-navigation"
      >
        <button 
          onClick={fetchFavoriteVendors}
          className="flex flex-col items-center justify-center w-1/3"
          data-section="favorites-tab"
        >
          <Heart 
            className="w-5 h-5" 
            fill={showingFavorites ? "#FF4E8E" : "none"} 
            stroke={showingFavorites ? "#FF4E8E" : "#5D5FEF"}
          />
          <span className="text-[8px] mt-0.5" style={{ color: showingFavorites ? "#FF4E8E" : "#5D5FEF" }}>
            Favorites
          </span>
        </button>
        
        <button 
          onClick={toggleMessages}
          className="flex flex-col items-center justify-center w-1/3"
          data-section="messages-tab"
        >
          <MessageCircle 
            className="w-5 h-5" 
            fill={showingMessages ? "#FF4E8E" : "none"} 
            stroke={showingMessages ? "#FF4E8E" : "#5D5FEF"}
          />
          <span className="text-[8px] mt-0.5" style={{ color: showingMessages ? "#FF4E8E" : "#5D5FEF" }}>
            Inquiries
          </span>
        </button>
      </div>
    );
  }
}

// Main ConnectMobile component
class ConnectMobile extends React.Component<ConnectMobileProps, ConnectMobileState> {
  state: ConnectMobileState = {
    showFilters: false,
    viewMode: 'list',
    favoriteVendors: [],
    showingFavorites: false,
    loadingFavorites: false,
    showingMessages: false,
    messages: [
      {
        id: '1',
        vendorName: 'Dreamy Venues',
        vendorType: 'Venues',
        message: 'Thank you for your inquiry! We have several beautiful venues available for your wedding date. Would you like to schedule a tour?',
        timestamp: '2h ago',
        isRead: false,
        vendorImage: '/themesOption/Indoor.jpg'
      },
      {
        id: '2',
        vendorName: 'Capture Moments Photography',
        vendorType: 'Photography',
        message: 'Hi! We\'d love to be part of your special day. Our premium package includes 8 hours of coverage and 500 edited photos.',
        timestamp: '5h ago',
        isRead: true,
        vendorImage: '/themesOption/Proposal.jpg'
      },
      {
        id: '3',
        vendorName: 'Floral Elegance',
        vendorType: 'Florists',
        message: 'Your vision for a romantic garden theme sounds perfect! We can create custom arrangements with your favorite flowers.',
        timestamp: '1d ago',
        isRead: true,
        vendorImage: '/themesOption/Bouquet.jpg'
      },
      {
        id: '4',
        vendorName: 'Gourmet Delights',
        vendorType: 'Catering',
        message: 'We offer a variety of menu options that can be customized to your preferences. Would you like to schedule a tasting?',
        timestamp: '2d ago',
        isRead: false,
        vendorImage: '/themesOption/Classic.jpg'
      },
      {
        id: '5',
        vendorName: 'Melody Makers',
        vendorType: 'Entertainment',
        message: 'Our live band can perform both ceremony and reception music. We have a diverse repertoire of songs to choose from.',
        timestamp: '3d ago',
        isRead: true,
        vendorImage: '/themesOption/Modern.jpg'
      }
    ],
    isViewTransitioning: false
  };

  // Function to fetch favorite vendors
  fetchFavoriteVendors = async () => {
    try {
      this.setState({ loadingFavorites: true });
      console.log('Fetching favorites...');
      const favorites = await FavoritesAPI.getFavoriteVendors();
      console.log('Favorites received:', favorites);
      this.setState({
        favoriteVendors: favorites,
        showingFavorites: true
      });
    } catch (error) {
      console.error('Error fetching favorite vendors:', error);
    } finally {
      this.setState({ loadingFavorites: false });
    }
  };

  setShowFilters = (show: boolean) => {
    this.setState({ showFilters: show });
  };

  setViewMode = (mode: 'list' | 'swipe') => {
    this.setState({ viewMode: mode });
  };

  setShowingFavorites = (show: boolean) => {
    this.setState({ showingFavorites: show });
    
    // When switching back from favorites to main view, force a re-render of the current category
    if (!show) {
      // Small delay to ensure state updates first
      setTimeout(() => {
        // Get current selected category and trigger a "re-selection" of it to refresh the view
        const currentCategory = this.props.selectedCategory;
        this.props.setSelectedCategory(currentCategory);
      }, 50);
    }
  };

  handleMessageClick = (message: any) => {
    // Handle message click - you can implement navigation to message details
    console.log('Message clicked:', message);
  };

  toggleMessages = () => {
    this.setState(prevState => ({
      showingMessages: !prevState.showingMessages,
      showingFavorites: false
    }));
  };

  render() {
    const {
      selectedCategory,
      setSelectedCategory,
      searchQuery,
      setSearchQuery,
      vendorData,
      isLoading,
      handleSelectVendor
    } = this.props;

    const {
      showFilters,
      viewMode,
      favoriteVendors,
      showingFavorites,
      loadingFavorites,
      showingMessages,
      messages
    } = this.state;

    return (
      <div className="h-full w-full flex flex-col overflow-hidden">
        {/* App Content */}
        <div className="app flex-1 flex flex-col relative" data-section="connect-mobile-app">
          <MainContent
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            showFilters={showFilters}
            setShowFilters={this.setShowFilters}
            vendorData={vendorData}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            viewMode={viewMode}
            setViewMode={this.setViewMode}
            showingFavorites={showingFavorites}
            setShowingFavorites={this.setShowingFavorites}
            favoriteVendors={favoriteVendors}
            loadingFavorites={loadingFavorites}
            isLoading={isLoading}
            handleSelectVendor={handleSelectVendor}
            showingMessages={showingMessages}
            messages={messages}
            onMessageClick={this.handleMessageClick}
            toggleMessages={this.toggleMessages}
          />
        </div>
        
        {/* Bottom Nav */}
        <BottomNav
          showingFavorites={showingFavorites}
          fetchFavoriteVendors={this.fetchFavoriteVendors}
          showingMessages={showingMessages}
          toggleMessages={this.toggleMessages}
        />
      </div>
    );
  }
}

export default ConnectMobile;